/**
 * Shared contract types (docs/GAME_DESIGN.md §5). Consumed by @sa/protocol, @sa/bots,
 * the server and the client. Changes here must stay additive.
 *
 * Conventions:
 * - Positions are map units (mu); velocities are mu per SECOND.
 * - Ticks are 10 Hz integers; `state.tick` is the tick most recently simulated.
 * - PlayerId is 1..8, 0 means neutral / nobody.
 * - Angles (hint bearing / spread) are radians, `atan2(dy, dx)` in map space (+y = south).
 */
import type { CountryId, HexIdx, RegionId, Vec2, WaterIdx, WorldMap } from './world.js';
import type { HexGeo } from './hex.js';

export type { CountryId, HexIdx, RegionId, Vec2, WaterIdx, WorldMap };

export type PlayerId = number;
export type TeamId = number;
export type EntityId = number;
export type Tick = number;

export type BotDifficulty = 'easy' | 'normal' | 'hard';
export type MatchMode = 'ffa' | 'teams';
export type SlotKind = 'open' | 'closed' | 'human' | 'bot';

// ---------------------------------------------------------------------------
// Lobby / match setup
// ---------------------------------------------------------------------------

export interface MatchSettings {
  mode: MatchMode;
  /** 2..8 */
  maxPlayers: number;
  /** teams mode only */
  teamCount?: number;
  private?: boolean;
  /** override of BALANCE.matchTicks (tests / harness) */
  matchTicks?: number;
}

/** A lobby slot. Occupied slots (`human` / `bot`) become players in slot order. */
export interface PlayerSlot {
  slot: number;
  kind: SlotKind;
  userId?: string;
  name: string;
  difficulty?: BotDifficulty;
  team: TeamId;
  regionId?: RegionId | null;
  ready: boolean;
}

/** A player in a running match. */
export interface PlayerInfo {
  id: PlayerId;
  name: string;
  team: TeamId;
  regionId: RegionId;
  isBot: boolean;
  difficulty?: BotDifficulty;
  userId?: string;
}

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export type StructureKind = 'radar' | 'sam' | 'silo' | 'city' | 'airbase';
export type AircraftKind = 'fighter' | 'bomber';
export type ShipKind = 'sub' | 'carrier';
export type MissileKind = 'icbm' | 'slbm';
export type EntityKind = StructureKind | AircraftKind | ShipKind | MissileKind | 'bomb' | 'interceptor';
/** Kinds accepted by `build` intents (ships are built at a port hex). */
export type BuildKind = StructureKind | ShipKind;

/** Wire order: index = u8 kind code. Append only. */
export const ENTITY_KINDS: readonly EntityKind[] = [
  'radar',
  'sam',
  'silo',
  'city',
  'airbase',
  'fighter',
  'bomber',
  'sub',
  'carrier',
  'icbm',
  'slbm',
  'bomb',
  'interceptor',
];

/** Coarse per-entity state as sent on the wire (`state u8`). Append only. */
export type EntityViewState = 'building' | 'active' | 'exposed' | 'loaded' | 'braced';
export const ENTITY_VIEW_STATES: readonly EntityViewState[] = ['building', 'active', 'exposed', 'loaded', 'braced'];

/** Reason an entity left a player's view. Append only (wire u8). */
export type RemoveReason = 'lostContact' | 'destroyed' | 'intercepted' | 'landed' | 'detonated';
export const REMOVE_REASONS: readonly RemoveReason[] = ['lostContact', 'destroyed', 'intercepted', 'landed', 'detonated'];

/** Linear motion segment: pos(t) = (x, y) + (vx, vy) · (t − tick) / TICK_HZ. */
export interface MotionSeg {
  x: number;
  y: number;
  vx: number;
  vy: number;
  tick: Tick;
}

interface EntityCommon {
  id: EntityId;
  owner: PlayerId;
  /** current position (mu) */
  x: number;
  y: number;
  createdTick: Tick;
  /**
   * Vision output (written by the vision system each tick): bit p set ⇔ player p's team
   * currently sees this entity (owner team always included). See `canSee()`.
   */
  visMask: number;
}

interface StructureCommon extends EntityCommon {
  hex: HexIdx;
  /** construction finishes at this tick (≤ current tick ⇒ active) */
  buildDoneTick: Tick;
}

export interface RadarEntity extends StructureCommon {
  kind: 'radar';
}

export interface SamEntity extends StructureCommon {
  kind: 'sam';
  ammo: number;
  /** tick the next reload completes, 0 when full */
  reloadAt: Tick;
  /** interceptor currently in flight, 0 if none */
  engaging: EntityId;
  /** tracked hostile missile this SAM engages first while it is in range, 0 if none */
  priorityId: EntityId;
  /**
   * emissions control: no own sensor (engages only what the team's sensors track, at half range),
   * never exposed by firing
   */
  silent: boolean;
  /** its engagement radar gave it away: visible to every player until this tick (after its last shot) */
  exposedUntil: Tick;
}

export interface SiloEntity extends StructureCommon {
  kind: 'silo';
  loaded: number;
  queued: number;
  /** tick the head of the load queue completes, 0 if queue empty */
  loadDoneTick: Tick;
  cooldownUntil: Tick;
  exposedUntil: Tick;
}

export interface CityEntity extends StructureCommon {
  kind: 'city';
  name: string;
  level: number;
  /** 0 when not upgrading */
  upgradeDoneTick: Tick;
  /** credits paid for the running upgrade (for 50% cancel refund) */
  upgradeCost: number;
  /** braced (brace intent) while tick < braceUntil */
  braceUntil: Tick;
  /** the city may brace again from this tick */
  braceReadyTick: Tick;
}

export interface AirbaseEntity extends StructureCommon {
  kind: 'airbase';
  autoScramble: boolean;
}

export type StructureEntity = RadarEntity | SamEntity | SiloEntity | CityEntity | AirbaseEntity;

export type AirMission = 'strike' | 'patrol' | 'scout';
/** Internal aircraft lifecycle. Only `airborne` phases are visible to other teams. */
export type AircraftPhase = 'building' | 'parked' | 'rearming' | 'outbound' | 'onStation' | 'intercept' | 'returning';

export interface AircraftEntity extends EntityCommon {
  kind: AircraftKind;
  /** home airbase or carrier */
  baseId: EntityId;
  phase: AircraftPhase;
  mission: AirMission | null;
  /** mission target point */
  tx: number;
  ty: number;
  /** intercept target (auto-scramble), 0 if none */
  targetId: EntityId;
  seg: MotionSeg;
  /** build / rearm completes at this tick */
  readyTick: Tick;
  /** fighter: air-to-air missiles; bomber: 1 = bomb aboard */
  ammo: number;
  stationUntil: Tick;
  nextShotTick: Tick;
  /** next heading refresh while chasing a moving point */
  steerTick: Tick;
}

export interface ShipCommon extends EntityCommon {
  buildDoneTick: Tick;
  hp: number;
  seg: MotionSeg;
  /** water hex last reached */
  wHex: WaterIdx;
  /** remaining waypoints (water hex indices), consumed from the front */
  path: WaterIdx[];
  /** final destination point (never sent to other teams) */
  destX: number;
  destY: number;
  /**
   * `docked()` as of the last naval tick: at rest by own land (SLBMs load there). Kept as a field
   * so a port lost to creep or a blast changes the ship's view like any other field.
   */
  inPort: boolean;
}

export interface SubEntity extends ShipCommon {
  kind: 'sub';
  slbms: number;
  slbmQueued: number;
  slbmLoadDoneTick: Tick;
  torps: number;
  torpReadyTick: Tick;
  torpRestockTick: Tick;
  exposedUntil: Tick;
}

export interface CarrierEntity extends ShipCommon {
  kind: 'carrier';
  samAmmo: number;
  samReloadAt: Tick;
  samEngaging: EntityId;
  autoScramble: boolean;
}

export type ShipEntity = SubEntity | CarrierEntity;

export interface MissileEntity extends EntityCommon {
  kind: MissileKind;
  launcherId: EntityId;
  ox: number;
  oy: number;
  tx: number;
  ty: number;
  launchTick: Tick;
  flightTicks: number;
  /** bit p set ⇔ player p has received a MissileTrack (persists until impact) */
  trackMask: number;
}

export interface BombEntity extends EntityCommon {
  kind: 'bomb';
  bomberId: EntityId;
  impactTick: Tick;
}

export interface InterceptorEntity extends EntityCommon {
  kind: 'interceptor';
  shooterId: EntityId;
  targetId: EntityId;
  ox: number;
  oy: number;
  fireTick: Tick;
  resolveTick: Tick;
  pk: number;
}

export type Entity =
  | StructureEntity
  | AircraftEntity
  | ShipEntity
  | MissileEntity
  | BombEntity
  | InterceptorEntity;

// ---------------------------------------------------------------------------
// Intents
// ---------------------------------------------------------------------------

export type Intent =
  | { type: 'focus'; hex: HexIdx | null }
  | { type: 'build'; kind: BuildKind; hex: HexIdx }
  | { type: 'cancel'; unitId: EntityId }
  | { type: 'upgradeCity'; unitId: EntityId }
  /** silo: ICBMs; sub (docked at an own port): SLBMs; SAM: interceptors (straight into its magazine) */
  | { type: 'buyMissile'; siloId: EntityId; count: number }
  /** silo or sub */
  | { type: 'launch'; launcherId: EntityId; x: number; y: number }
  /** airbase (fighter/bomber) or carrier (fighter) */
  | { type: 'buildAircraft'; baseId: EntityId; kind: AircraftKind }
  | { type: 'air'; baseId: EntityId; kind: AircraftKind; mission: AirMission; x: number; y: number }
  | { type: 'airCmd'; aircraftId: EntityId; cmd: 'recall' | 'retarget'; x?: number; y?: number }
  /** `queue`: append a waypoint after the current route (shift + right-click) instead of replacing it */
  | { type: 'move'; unitId: EntityId; x: number; y: number; queue?: boolean }
  | { type: 'autoScramble'; baseId: EntityId; on: boolean }
  | { type: 'ally.request'; to: PlayerId }
  | { type: 'ally.respond'; from: PlayerId; accept: boolean }
  | { type: 'ally.break'; with: PlayerId }
  /** a timed mutual non-aggression (FFA): no creep pressure and no strikes between the two */
  | { type: 'ceasefire.request'; to: PlayerId }
  | { type: 'ceasefire.respond'; from: PlayerId; accept: boolean }
  /** end a ceasefire early: announced to everyone, the breaker is locked out of new ones for a while */
  | { type: 'ceasefire.break'; with: PlayerId }
  /** send credits to another nation (`BALANCE.diplomacy.giftAmounts`) */
  | { type: 'gift'; to: PlayerId; amount: number }
  /** mark a nation as the target for our ally / teammates */
  | { type: 'mark'; target: PlayerId }
  /** brace an own city for BALANCE.brace.ticks (halved blast pop loss, no level loss) */
  | { type: 'brace'; cityId: EntityId }
  /** an own SAM engages this tracked hostile missile first (null clears) */
  | { type: 'samPriority'; samId: EntityId; targetId: EntityId | null }
  /** a land SAM's emissions control */
  | { type: 'samMode'; samId: EntityId; silent: boolean }
  | { type: 'surrender' };

export type IntentType = Intent['type'];

export interface PlayerIntent {
  player: PlayerId;
  intent: Intent;
  /** client sequence number, echoed in IntentResult */
  seq?: number;
}

export type IntentError =
  | 'matchOver'
  | 'unknownPlayer'
  | 'eliminated'
  | 'badIntent'
  | 'badHex'
  | 'badTarget'
  | 'notFound'
  | 'notOwner'
  | 'badKind'
  | 'credits'
  | 'cap'
  | 'notOwnHex'
  | 'occupied'
  | 'radiation'
  | 'spacing'
  | 'notPort'
  | 'notBuilding'
  | 'notReady'
  | 'maxLevel'
  | 'busy'
  | 'full'
  | 'empty'
  | 'notDocked'
  | 'releaseLock'
  | 'inFlightCap'
  | 'allyTarget'
  | 'betrayalLock'
  | 'range'
  | 'cooldown'
  | 'noAircraft'
  | 'unreachable'
  | 'allianceMode'
  | 'allianceSize'
  | 'allianceCooldown'
  | 'noRequest'
  | 'alreadyAllied'
  | 'notAllied'
  | 'self'
  /** the target is a nation we have a ceasefire with */
  | 'ceasefire'
  /** we broke a ceasefire (or the proposer did): no new one yet */
  | 'ceasefireCooldown'
  | 'noCeasefire';

export type IntentResult = { ok: true } | { ok: false; err: IntentError };

/** Result of one intent inside `step`, aligned with the input array. */
export interface StepIntentResult {
  player: PlayerId;
  seq?: number;
  result: IntentResult;
}

// ---------------------------------------------------------------------------
// Fog-of-war payloads (one per observer)
// ---------------------------------------------------------------------------

export interface MissileTrack {
  id: EntityId;
  kind: MissileKind;
  owner: PlayerId;
  ox: number;
  oy: number;
  tx: number;
  ty: number;
  launchTick: Tick;
  flightTicks: number;
}

/** "LAUNCH DETECTED / NO TRACK". No link to the missile id. */
export interface LaunchHint {
  hintId: number;
  x: number;
  y: number;
  /** radians, jittered true bearing origin→target */
  bearing: number;
  /** half-angle of the wedge, radians */
  spread: number;
  tick: Tick;
}

export interface Detonation {
  x: number;
  y: number;
  tick: Tick;
  /** true if the impact point was inside the observer's vision */
  exact: boolean;
}

export interface InterceptEvent {
  shooterId: EntityId;
  targetId: EntityId;
  fireTick: Tick;
  resolveTick: Tick;
  /** set on resolve */
  hit?: boolean;
  /**
   * A shot at our side's aircraft from a shooter we cannot see (`shooterId` 0): a point a little
   * way from the aircraft toward where it came from — the direction only, never the shooter.
   */
  ox?: number;
  oy?: number;
}

export type PhaseKind =
  | 'release'
  | 'final'
  | 'beacon'
  | 'eliminated'
  | 'ally'
  | 'allyBroken'
  | 'capitalsExposed'
  | 'capitalCritical'
  | 'capitalRecovered'
  | 'allyDeclined'
  | 'ceasefireOffer'
  | 'ceasefire'
  | 'ceasefireDeclined'
  | 'ceasefireBroken'
  | 'gift'
  | 'mark';

/**
 * release / final / capitalsExposed: a = b = 0. beacon: a = leader. eliminated: a = player,
 * b = 1 if the player lost their capital (decapitation) — on the wire the protocol adds the killer
 * above that bit (`eliminatedPhaseB` / `eliminatedFromB` in @sa/protocol: `b >> 1`, 0 none). ally: a, b = new pair.
 * allyBroken: a = breaker, b = ex-ally. capitalCritical (§1.11 second life): a = player whose
 * capital is critical, b = window in seconds — critical until `tick + 10·b` unless hit again (a
 * later `capitalCritical` restarts it). capitalRecovered: a = player, b = 0. allyDeclined: a = proposer,
 * b = the player who declined (only those two receive it).
 * Diplomacy: ceasefireOffer: a = proposer, b = asked, the offer runs out at
 * `tick + diplomacy.ceasefireRequestTicks` (only those two). ceasefire: a < b = the pair, it holds until
 * `tick + diplomacy.ceasefireTicks` (public). ceasefireDeclined: a = proposer, b = decliner (only those two).
 * ceasefireBroken: a = breaker, b = the other (public). gift: a = sender, b = amount / 100 (only the
 * recipient; the filter builds it from the sim's `gift` event). mark: a = marker, b = the marked nation
 * (only the marker's side: itself, its ally / teammates).
 */
export interface PhaseEvent {
  kind: PhaseKind;
  a: number;
  b: number;
  tick: Tick;
}

export interface AllyRequest {
  from: PlayerId;
  to: PlayerId;
  expiresTick: Tick;
}

export interface Ceasefire {
  /** the pair, a < b */
  a: PlayerId;
  b: PlayerId;
  untilTick: Tick;
}

export interface AllianceState {
  /** indexed by PlayerId (0 unused): current FFA ally or 0. Always 0 in teams mode. */
  allyOf: number[];
  requests: AllyRequest[];
  /** breaker may not target / creep into `target` until `untilTick` (a broken alliance or ceasefire) */
  betrayals: { breaker: PlayerId; target: PlayerId; untilTick: Tick }[];
  /** indexed by PlayerId: may not form a new alliance before this tick */
  noAllyUntil: number[];
  /** timed non-aggression pairs (FFA; any number per player) */
  ceasefires: Ceasefire[];
  ceasefireRequests: AllyRequest[];
  /** indexed by PlayerId: may not propose / accept a ceasefire before this tick (broke one) */
  noCeasefireUntil: number[];
  /** indexed by PlayerId: may not mark a target for the ally before this tick */
  markReadyTick: number[];
}

/** Last-known record of an entity that left vision (client / bot memory). */
export interface Ghost {
  id: EntityId;
  kind: EntityKind;
  owner: PlayerId;
  x: number;
  y: number;
  /** heading (radians) for mobile units */
  heading?: number;
  /** tick last seen */
  tick: Tick;
  /** city level / ammo, as last seen */
  extra?: number;
}

/** Extra details only ever sent to the entity's owner. */
export interface OwnEntityDetail {
  buildDoneTick?: Tick;
  /** silo: ICBMs loaded / queued; sub: SLBMs */
  loaded?: number;
  queued?: number;
  loadDoneTick?: Tick;
  cooldownUntil?: Tick;
  ammo?: number;
  reloadAt?: Tick;
  level?: number;
  upgradeDoneTick?: Tick;
  autoScramble?: boolean;
  /** SAM: emissions control */
  silent?: boolean;
  /** aircraft */
  baseId?: EntityId;
  phase?: AircraftPhase;
  mission?: AirMission | null;
  tx?: number;
  ty?: number;
  readyTick?: Tick;
  stationUntil?: Tick;
  /** ships */
  hp?: number;
  torps?: number;
  destX?: number;
  destY?: number;
  /** ship: remaining waypoints [x0, y0, x1, y1, …] (the water route to destX / destY) */
  route?: number[];
  /** sub: at rest on an own coast, where SLBMs load (§1.7) */
  docked?: boolean;
  exposedUntil?: Tick;
  /** city: braced until / may brace again from */
  braceUntil?: Tick;
  braceReadyTick?: Tick;
  /** SAM: missile it engages first (0 none) */
  priorityId?: EntityId;
}

/** What a player sees of one entity (snapshot `upserts` row). */
export interface EntityView {
  id: EntityId;
  kind: EntityKind;
  owner: PlayerId;
  state: EntityViewState;
  x: number;
  y: number;
  vx: number;
  vy: number;
  segTick: Tick;
  /** ammo / level (u8) */
  extra: number;
  /** owner only */
  detail?: OwnEntityDetail;
}

/** Focused-front status (§1.13): none = no focus / nothing to push in the zone. */
export type FrontStatus = 'none' | 'advancing' | 'stalled' | 'losing';
export const FRONT_STATUSES: readonly FrontStatus[] = ['none', 'advancing', 'stalled', 'losing'];

/** Readout of the player's focused front (owner only, snapshot header). */
export interface FrontReadout {
  status: FrontStatus;
  /** pressure ratio of our attack in the focus zone (Σatt / Σdef), 0..6.3 */
  ratio: number;
  /** net hexes per minute gained (+) / lost (−) inside the focus zone, ~30 s average */
  rate: number;
}

export interface SnapshotHeader {
  tick: Tick;
  credits: number;
  incomePerMin: number;
  /** 0..1 */
  areaShare: number;
  /** bit 0: released, bit 1: final phase, bit 2: eliminated (spectating), bit 3: capitals exposed */
  flags: number;
}

export const SNAP_FLAG_RELEASED = 1;
export const SNAP_FLAG_FINAL = 2;
export const SNAP_FLAG_ELIMINATED = 4;
export const SNAP_FLAG_CAPITALS_EXPOSED = 8;

/** Decoded `snap` / `full` message (see docs §3). */
export interface Snapshot {
  full: boolean;
  hdr: SnapshotHeader;
  owners: { hex: HexIdx; owner: PlayerId }[];
  /** dens / rad are 0..15 nibbles */
  popRad: { hex: HexIdx; dens: number; rad: number }[];
  upserts: EntityView[];
  removes: { id: EntityId; reason: RemoveReason }[];
  tracks: MissileTrack[];
  hints: LaunchHint[];
  detonations: Detonation[];
  intercepts: InterceptEvent[];
  /** every player's public standing (1 Hz) */
  shares: ShareEntry[];
  phase: PhaseEvent[];
}

/**
 * One player's public standing, broadcast to everyone at 1 Hz (scoreboards are public): its land
 * share and its score (§1.9) — the very number the standings rank at 20:00.
 */
export interface ShareEntry {
  playerId: PlayerId;
  areaShare: number;
  score: number;
}

/** A player's (or bot's) fog-limited knowledge, produced by `applySnapshot`. */
export interface PlayerView {
  tick: Tick;
  me: PlayerId;
  team: TeamId;
  mode: MatchMode;
  players: PlayerInfo[];
  credits: number;
  incomePerMin: number;
  areaShare: number;
  /** every player's public standing (1 Hz): land share and score */
  shares: ShareEntry[];
  /** per land hex (N): last known owner, density / radiation nibbles, last seen tick (-1 never) */
  owner: Uint8Array;
  dens: Uint8Array;
  rad: Uint8Array;
  lastSeenTick: Int32Array;
  /** 1 if the hex is live this tick */
  live: Uint8Array;
  entities: Map<EntityId, EntityView>;
  ghosts: Map<EntityId, Ghost>;
  tracks: Map<EntityId, MissileTrack>;
  hints: LaunchHint[];
  detonations: Detonation[];
  intercepts: InterceptEvent[];
  phases: PhaseEvent[];
  allies: PlayerId[];
  allyRequests: AllyRequest[];
  /** our ceasefires: the other nation → the tick it ends */
  ceasefires: Map<PlayerId, Tick>;
  /** ceasefire offers to / from us that are still open */
  ceasefireRequests: AllyRequest[];
  focus: HexIdx | null;
  released: boolean;
  finalPhase: boolean;
  /** eliminated: the player spectates (FFA, or once the whole team is out: full map vision) */
  eliminated: boolean;
  /** 10:00 passed: capitals can be destroyed */
  capitalsExposed: boolean;
  /** players whose capital is critical (§1.11 second life) → tick the window ends unless hit again */
  criticalCapitals: Map<PlayerId, Tick>;
  /** own focused-front readout */
  front: FrontReadout;
  beaconLeader: PlayerId;
  result: MatchResult | null;
}

// ---------------------------------------------------------------------------
// Sim events (server-side, pre-filter). Per-observer payloads are precomputed so
// the network filter only has to pick the observer's row.
// ---------------------------------------------------------------------------

export interface ObserverHint {
  observer: PlayerId;
  hint: LaunchHint;
}

export interface ObserverDetonation {
  observer: PlayerId;
  det: Detonation;
}

/** Where a shot flew: from the shooter (sx, sy) to the target (tx, ty), at the event. */
export interface ShotWhere {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
}

export type SimEvent =
  /** Missile launched. `hints` has one row per non-allied player. */
  | { type: 'launch'; missileId: EntityId; kind: MissileKind; owner: PlayerId; tick: Tick; hints: ObserverHint[] }
  /** First tick the missile is inside these players' (team) vision → send MissileTrack. */
  | { type: 'track'; missileId: EntityId; players: PlayerId[]; track: MissileTrack }
  /** Warhead (ICBM / SLBM / bomb) detonated. `views` has one row per player. */
  | { type: 'detonation'; owner: PlayerId; kind: MissileKind | 'bomb'; x: number; y: number; tick: Tick; views: ObserverDetonation[] }
  /** a shot at a missile or an aircraft (`where`: shooter and target positions then — for fog-safe hints) */
  | { type: 'interceptFire'; shooterId: EntityId; shooterOwner: PlayerId; targetId: EntityId; fireTick: Tick; resolveTick: Tick; where?: ShotWhere }
  | { type: 'interceptResolve'; shooterId: EntityId; shooterOwner: PlayerId; targetId: EntityId; fireTick: Tick; resolveTick: Tick; hit: boolean; where?: ShotWhere }
  /** Entity left the world. Filter maps to `removes` for observers that currently see it. */
  | { type: 'removed'; id: EntityId; kind: EntityKind; owner: PlayerId; reason: RemoveReason }
  /** Aircraft landed at its base (it stays in the world, parked, invisible to other teams). */
  | { type: 'landed'; id: EntityId; kind: AircraftKind; owner: PlayerId }
  | { type: 'built'; id: EntityId; kind: EntityKind; owner: PlayerId }
  | { type: 'cityCaptured'; id: EntityId; hex: HexIdx; from: PlayerId; to: PlayerId; level: number }
  | { type: 'phase'; phase: PhaseEvent }
  | { type: 'allyPrompt'; from: PlayerId; to: PlayerId; expiresTick: Tick }
  /** credits sent from one nation to another (the filter tells only the recipient) */
  | { type: 'gift'; from: PlayerId; to: PlayerId; amount: number; tick: Tick }
  /** Leader beacon: every player receives the leader's cities as ghosts. */
  | { type: 'beacon'; leader: PlayerId; cities: Ghost[] }
  | { type: 'eliminated'; player: PlayerId; surrendered: boolean }
  | { type: 'end'; result: MatchResult };

export type SimEventType = SimEvent['type'];

export interface StepResult {
  events: SimEvent[];
  results: StepIntentResult[];
}

// ---------------------------------------------------------------------------
// Results & stats
// ---------------------------------------------------------------------------

export type EndReason = 'elimination' | 'time' | 'domination' | 'surrender';

export interface Standing {
  playerId: PlayerId;
  score: number;
  areaShare: number;
  popShare: number;
  /** kill credit share (§1.17); always set by the sim */
  killShare?: number;
  eliminatedTick?: Tick;
}

export interface MatchResult {
  reason: EndReason;
  winners: PlayerId[];
  standings: Standing[];
  tick: Tick;
}

export type SpendKind =
  | 'radar'
  | 'sam'
  | 'silo'
  | 'city'
  | 'cityUpgrade'
  | 'airbase'
  | 'fighter'
  | 'bomber'
  | 'bomberRearm'
  | 'sub'
  | 'carrier'
  | 'icbm'
  | 'slbm'
  /** SAM interceptors bought straight into the magazine */
  | 'interceptor'
  | 'brace'
  /** credits sent to another nation */
  | 'gift';

export interface PlayerStats {
  id: PlayerId;
  region: RegionId;
  difficulty?: BotDifficulty;
  score: number;
  /** area share sampled every 30 s */
  areaCurve: number[];
  /** pop sampled every 30 s */
  popCurve: number[];
  eliminatedTick: Tick | null;
  creditsEarned: number;
  spendByKind: Partial<Record<SpendKind, number>>;
  icbmLaunched: number;
  slbmLaunched: number;
  bombsDropped: number;
  /** own ICBMs / SLBMs shot down */
  intercepted: number;
  /** own warheads that detonated */
  hits: number;
  citiesLost: number;
  /** hexes taken from other players (not neutral) */
  contestedGains: number;
  /** ticks this player spent in contact with an enemy player */
  contactTicks: number;
  /** pop killed by own warheads on rivals' land (always set by the sim) */
  popKilled?: number;
  /** braces used / hits taken while braced (always set by the sim) */
  braces?: number;
  bracedHits?: number;
  /** eliminated by losing the capital (decapitation; always set by the sim) */
  decapitated?: boolean;
  /** times the capital went critical / recovered (§1.11 second life; always set by the sim) */
  capitalCriticals?: number;
  capitalRecoveries?: number;
}

/** The sim's own per-player stats record: every pass-1 field present. */
export type SimPlayerStats = PlayerStats & Required<Pick<PlayerStats, 'popKilled' | 'braces' | 'bracedHits' | 'decapitated' | 'capitalCriticals' | 'capitalRecoveries'>>;

export interface MatchStats {
  seed: number;
  mapVersion: string;
  players: PlayerStats[];
  durationTicks: number;
  endReason: EndReason;
  winnerIds: PlayerId[];
  /** first tick two players' territories touched, null if never */
  firstContactTick: Tick | null;
  /** neutral area share sampled every 30 s */
  neutralCurve: number[];
}

// ---------------------------------------------------------------------------
// Authoritative state
// ---------------------------------------------------------------------------

export interface PlayerState extends PlayerInfo {
  /** capacity multiplier on owned land (start region capScale of the match's region set) */
  capScale: number;
  /** start region capital: lost (ruined or captured) from 10:00 ⇒ eliminated */
  capitalHex: HexIdx;
  /** capital second life (§1.11): critical while tick < capitalCriticalUntil; went critical at capitalCriticalFrom (-1: never) */
  capitalCriticalFrom: Tick;
  capitalCriticalUntil: Tick;
  /** focused-front readout, refreshed by the creep system */
  front: FrontReadout;
  credits: number;
  incomePerMin: number;
  focusHex: HexIdx | -1;
  focusSetTick: Tick;
  /** -1 while alive */
  eliminatedTick: Tick;
  surrendered: boolean;
  // aggregates refreshed by the scoring system every tick
  hexCount: number;
  cityCount: number;
  areaW: number;
  pop: number;
  areaShare: number;
  popShare: number;
  /** share of all pop killed on rivals' land that this player's warheads killed */
  killShare: number;
  score: number;
}

/**
 * Vision output, rewritten every tick by `systems/vision.ts`. All per-hex arrays hold one
 * bit per *player's own* sensors / territory; team union happens via `teamMask`:
 *   hex h is live for p ⇔ (live[h] & teamMask[p]) !== 0          (see `isHexLive`)
 *   entity e is visible to p ⇔ (e.visMask >> p) & 1               (see `canSee`)
 */
export interface VisionState {
  /** indexed by PlayerId: bitset of p and its allies / teammates */
  teamMask: Uint16Array;
  /** per land hex: bit p ⇔ owned by p or one ring outside p's border */
  terr: Uint16Array;
  /** per land hex: bit p ⇔ inside one of p's air/surface sensor discs */
  cover: Uint16Array;
  /** terr | cover (spectators: every hex) */
  live: Uint16Array;
  /** bit p ⇔ p is a spectator with full map vision (eliminated; teams: whole team out) */
  spectators: number;
  /** internal: structure-sensor part of `cover` (rebuilt per player when its sensor set changes) */
  staticCover: Uint16Array;
  /** per-player entity ids that entered / left visibility this tick (indexed by PlayerId) */
  entered: EntityId[][];
  left: EntityId[][];
}

export interface StatsAccumulator {
  firstContactTick: Tick | null;
  neutralCurve: number[];
  players: SimPlayerStats[];
}

export interface MatchState {
  world: WorldMap;
  geo: HexGeo;
  settings: MatchSettings;
  seed: number;
  matchTicks: number;
  tick: Tick;
  /** indexed by PlayerId − 1 */
  players: PlayerState[];

  // ---- dynamic per-hex SoA (N) ----
  owner: Uint8Array;
  pop: Float32Array;
  rad: Float32Array;
  prog: Float32Array;
  progBy: Uint8Array;
  flipTick: Int32Array;
  /** structure entity id on the hex (cities included), -1 if none */
  structId: Int32Array;
  cityLvl: Uint8Array;
  /** 1 ⇔ some non-allied player borders this hex (refreshed by creep) */
  frontier: Uint8Array;
  /** BFS distance (hexes) to the owner's focus front, 255 = none */
  focusDist: Uint8Array;
  /** derived from cityLvl */
  cityMult: Float32Array;

  entities: Map<EntityId, Entity>;
  nextId: EntityId;
  nextHintId: number;
  nextDetonationId: number;
  /** xoshiro128** state of the `combat` stream */
  combatRng: Uint32Array;

  alliances: AllianceState;
  vision: VisionState;
  /** bit p set ⇔ p's static sensor set changed (vision recomputes p's structure discs) */
  sensorsDirty: number;
  /** domination tracking */
  domination: { holder: number; sinceTick: Tick };
  stats: StatsAccumulator;
  result: MatchResult | null;
}
